import { useEffect } from "react";
import { SOCIALS } from "@/components/site/SocialLinks";
import { EN_BY_FR } from "@/lib/enPages";

export const SITE_URL = (process.env.REACT_APP_CANONICAL_URL || window.location.origin).replace(/\/$/, "");
export const absUrl = (p) => (!p ? undefined : /^https?:/.test(p) ? p : `${SITE_URL}${p.startsWith("/") ? "" : "/"}${p}`);
const DEFAULT_IMAGE = "/brand/logo.png";

function setTag(selector, create, attr, value) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function meta(key, content, prop = false) {
  const a = prop ? "property" : "name";
  setTag(`meta[${a}="${key}"]`, () => { const m = document.createElement("meta"); m.setAttribute(a, key); return m; }, "content", content);
}

/** Meta description ≤ 160 caractères (au-delà, Google la coupe) : fin de phrase si possible, sinon fin de mot + « … ». Même règle dans scripts/prerender.mjs. */
export function metaDesc(s, n = 160) {
  const t = String(s || "").replace(/\s+/g, " ").trim();
  if (t.length <= n) return t;
  const cut = t.slice(0, n);
  const end = cut.lastIndexOf(". ");
  return end >= 100 ? cut.slice(0, end + 1) : `${cut.slice(0, n - 1).replace(/[\s,;:]+\S*$/, "")}…`;
}

/** Per-page title, description, canonical, robots, Open Graph and JSON-LD. */
export function useSeo({ title, description, path, image, noindex = false, jsonLd = null, type = "website", lang = "fr", alternates = null }) {
  const ld = jsonLd ? JSON.stringify(jsonLd) : "";
  useEffect(() => {
    if (!title) return undefined;
    const url = `${SITE_URL}${path || window.location.pathname}`;
    const img = absUrl(image || DEFAULT_IMAGE);
    document.title = title;
    const desc = metaDesc(description);
    meta("description", desc);
    meta("robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large");
    meta("og:title", title, true);
    meta("og:description", desc, true);
    meta("og:url", url, true);
    meta("og:type", type, true);
    meta("og:image", img, true);
    meta("og:site_name", "Ozentya", true);
    meta("og:locale", lang === "en" ? "en_GB" : "fr_FR", true);
    document.documentElement.lang = lang;
    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((l) => l.remove());
    const p = path || window.location.pathname;
    const prod = p.match(/^\/(?:produit|en\/product)\/([^/?#]+)/)?.[1];
    const alt = alternates || (prod ? { fr: `/produit/${prod}`, en: `/en/product/${prod}` }
      : p === "/boutique" || p === "/en/shop" ? { fr: "/boutique", en: "/en/shop" } : EN_BY_FR[p] ? { fr: p, en: EN_BY_FR[p] } : null);
    if (alt) {
      for (const [hl, href] of [...Object.entries(alt), ["x-default", alt.fr]]) {
        const l = document.createElement("link");
        l.rel = "alternate"; l.hreflang = hl; l.href = `${SITE_URL}${href === "/" ? "/" : href}`;
        document.head.appendChild(l);
      }
    }
    meta("twitter:card", "summary_large_image");
    setTag('link[rel="canonical"]', () => { const l = document.createElement("link"); l.rel = "canonical"; return l; }, "href", url);
    let s = document.getElementById("seo-jsonld");
    if (ld) {
      if (!s) {
        s = document.createElement("script");
        s.type = "application/ld+json";
        s.id = "seo-jsonld";
        document.head.appendChild(s);
      }
      s.textContent = ld;
    } else if (s) {
      s.remove();
    }
    return undefined;
  }, [title, description, path, image, noindex, ld, type, lang, alternates]);
}

export const ORGANIZATION = {
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: "Ozentya",
  alternateName: ["Ozentya Paris", "Ozentya cheveux"],
  url: `${SITE_URL}/`,
  logo: { "@type": "ImageObject", url: absUrl("/brand/logo.png") },
  image: absUrl("/brand/logo.png"),
  description: "Ozentya, marque familiale antillaise de soins capillaires fondée en 2011, spécialiste de la chute de cheveux et de l'alopécie chez la femme afro : soins pour cheveux afro, crépus et naturels, pousse des cheveux, cuir chevelu, locks, barbe et cheveux des enfants.",
  foundingDate: "2011",
  brand: { "@type": "Brand", name: "Ozentya", logo: absUrl("/brand/logo.png") },
  knowsAbout: ["cheveux afro", "cheveux crépus", "cheveux nappy", "cheveux naturels", "pousse des cheveux", "cuir chevelu", "locks", "soin de la barbe"],
  address: { "@type": "PostalAddress", addressLocality: "Paris", addressCountry: "FR" },
  areaServed: ["FR", "GP", "MQ", "GF", "RE", "YT"],
  sameAs: [SOCIALS.instagramParis, SOCIALS.instagram, SOCIALS.tiktok, SOCIALS.pro].filter(Boolean),
};

const rate = (value) => ({ "@type": "MonetaryAmount", value, currency: "EUR" });
const qv = (min, max) => ({ "@type": "QuantitativeValue", minValue: min, maxValue: max, unitCode: "DAY" });
const days = (min, max) => ({ "@type": "ShippingDeliveryTime", handlingTime: qv(0, 1), transitTime: qv(min, max) });
const EU = ["BE", "LU", "NL", "ES", "PT", "DE", "AT", "IT", "PL"];

/** Frais réels pour un soin seul (grille backend SHIPPING : Outre-mer 11,90 € jusqu'à 30 €, 14,99 € au-delà). */
export const shippingDetails = (price) => [
  { "@type": "OfferShippingDetails", shippingLabel: "Mondial Relay — Point Relais ou Locker", shippingRate: rate(0), shippingDestination: { "@type": "DefinedRegion", addressCountry: "FR" }, deliveryTime: days(3, 4) },
  { "@type": "OfferShippingDetails", shippingLabel: "Mondial Relay Europe — Point Relais ou Locker", shippingRate: rate(4.99), shippingDestination: EU.map((c) => ({ "@type": "DefinedRegion", addressCountry: c })), deliveryTime: days(4, 7) },
  ...["GP", "MQ", "GF", "RE", "YT", "PM", "BL", "MF", "PF", "NC", "WF"].map((c) => ({
    "@type": "OfferShippingDetails", shippingLabel: "Colissimo Outre-mer", shippingRate: rate(Number(price) < 30.01 ? 11.9 : 14.99), shippingDestination: { "@type": "DefinedRegion", addressCountry: c }, deliveryTime: days(5, 9),
  })),
];

/** Politique /remboursement-retractation : 14 jours, produits non ouverts, retour par courrier à la charge de la cliente. */
export const RETURN_POLICY = {
  "@type": "MerchantReturnPolicy", applicableCountry: ["FR", ...EU], returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
  merchantReturnDays: 14, returnMethod: "https://schema.org/ReturnByMail", returnFees: "https://schema.org/ReturnFeesCustomerResponsibility",
  itemCondition: "https://schema.org/NewCondition", merchantReturnLink: `${SITE_URL}/remboursement-retractation`,
};
ORGANIZATION.hasMerchantReturnPolicy = RETURN_POLICY;

/** Fondateur, auteur des guides (mêmes données dans scripts/prerender.mjs). */
export const FOUNDER = {
  "@type": "Person", "@id": `${SITE_URL}/#pascal-chaubo`, name: "Pascal Chaubo", jobTitle: "Fondateur d'Ozentya", url: `${SITE_URL}/a-propos`,
  worksFor: { "@id": `${SITE_URL}/#organization` }, knowsAbout: ["soins des cheveux afro et crépus", "chute de cheveux chez la femme afro", "soins des locks", "soin de la barbe"],
};
ORGANIZATION.founder = FOUNDER;
